import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { HlmCard } from '@lite-workspace/shared/helm-ui/card';
import { HlmSwitch } from '@lite-workspace/shared/helm-ui/switch';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { NotificationsStore } from './notifications.store';
import { NOTIFICATIONS_HOST_PRODUCT, pickPreferenceEvents } from './notifications.models';

// Per-event email switches, grouped by product (spec 17 §3.2). The grid comes
// straight from the server catalog — every event defaults to ON; a switch off is
// the only stored preference. Mails without a switch are simply not in the catalog.
@Component({
  selector: 'app-notification-preferences',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmButton, HlmCard, HlmSwitch],
  template: `
    <div class="flex flex-col gap-6">
      @switch (store.preferencesStatus()) {
        @case ('loading') {
          <p class="py-10 text-center text-sm text-c3">Загрузка…</p>
        }
        @case ('error') {
          <div class="flex flex-col items-center gap-3 py-10">
            <p class="text-sm text-c3">Не удалось загрузить настройки.</p>
            <button
              hlmBtn
              type="button"
              variant="outline"
              class="h-8 rounded-full px-3 text-sm"
              (click)="store.loadPreferences()"
            >
              Повторить
            </button>
          </div>
        }
        @default {
          @for (group of groups(); track group.product) {
            <!-- A product with no switchable mail yet has nothing to show. -->
            @if (group.events.length) {
              <section hlmCard class="block gap-0 p-6 md:p-7">
                <h2 class="text-lg font-semibold tracking-[var(--ls-tight)] text-ink">{{ group.label }}</h2>
                <p class="mt-2 text-sm text-c2">Присылать письмо, когда:</p>
                <div class="mt-4 flex flex-col gap-4">
                  @for (event of group.events; track event.eventType) {
                    <div class="flex items-start justify-between gap-4">
                      <span class="text-sm text-ink">{{ event.label }}</span>
                      <hlm-switch
                        [checked]="event.emailEnabled"
                        (checkedChange)="toggle(group.product, event.eventType, $event)"
                        [aria-label]="event.label"
                      />
                    </div>
                  }
                </div>
              </section>
            }
          }
        }
      }
    </div>
  `,
})
export class NotificationPreferencesComponent {
  protected readonly store = inject(NotificationsStore);
  private readonly toast = inject(ToastService);
  // Gallery and photobook mount only the settings page and provide no token.
  private readonly hostProduct = inject(NOTIFICATIONS_HOST_PRODUCT, { optional: true });

  /** Events to show, in order; `null` = the whole server catalog. */
  readonly eventTypes = input<readonly string[] | null>(null);

  // The cabinet's own product leads; the rest keep catalog order.
  protected readonly groups = computed(() => {
    const groups = pickPreferenceEvents(this.store.preferences(), this.eventTypes());
    return [...groups.filter(g => g.product === this.hostProduct), ...groups.filter(g => g.product !== this.hostProduct)];
  });

  constructor() {
    void this.store.loadPreferences();
  }

  // The switch flips at once; the toast says whether the server kept it (a failed save flips it back).
  protected async toggle(product: string, eventType: string, emailEnabled: boolean): Promise<void> {
    if (await this.store.togglePreference(product, eventType, emailEnabled)) this.toast.success('Изменения сохранены');
    else this.toast.error('Не удалось сохранить изменения');
  }
}
